import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Select, Space, Tooltip, Typography } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useInviteMembers } from "../hooks/useInviteMembers";
import { useTrack } from "../../host/useTrack";
import { TrackOnClick } from "../../host/TrackOnClick";
import type { Account, TeamRole } from "../types";
import styles from "./InviteMemberModal.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MY_TEAM = "My Team";

// AHA-41755/AHA-43051: invites are capped by team size, NOT licence count.
// Deliberately high — it only guards against runaway growth; a licence
// shortage is a non-blocking warning, not a gate on inviting.
const MAX_TEAM_SIZE = 200_000;

export interface InviteMemberModalProps {
  open: boolean;
  account: Account | null;
  // Current team size (active members + pending invites) — the basis for the
  // MAX_TEAM_SIZE cap. Licences no longer gate invites.
  teamSize: number;
  // AHA-43204: owner-only (not owner-or-admin) so the role dropdown matches
  // the upstream gate in stpancras-general-api's invitation.controller.js,
  // which rejects admin-invites-admin with 400 "Cannot invite another admin".
  // The only thing this prop drives is the "Admin" option in `roleOptions`
  // below; the modal's other admin-vs-editor decisions live upstream.
  currentUserIsTeamOwner: boolean;
  // AHA-43204: gate the "Assign licences" checkbox + licence-availability
  // text on the team being on a paid plan. Free teams ship with a non-zero
  // licenseQuantity (200 today), so a pure quota gate leaked the block
  // through; the plan flag is the only correct signal.
  isPaidTeamPlan: boolean;
  onClose: () => void;
  onInvited: () => void;
  onBuyLicenses: () => void;
}

export function InviteMemberModal({
  open,
  account,
  teamSize,
  currentUserIsTeamOwner,
  isPaidTeamPlan,
  onClose,
  onInvited,
  onBuyLicenses,
}: InviteMemberModalProps) {
  const { t } = useTranslation();
  const { invite, pending, error, clearError } = useInviteMembers();
  const track = useTrack();
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<TeamRole>("editor");
  const [teamName, setTeamName] = useState("");
  // AHA-41755: "Assign licences" intent. Persisted by the backend as an
  // intent row keyed on the upstream invitationId; on accept, that flag
  // gates whether a V2 licence is granted. Default-on (true) when there's
  // at least one seat available — the modal's open-effect below seeds the
  // value at open time, so the initial state here only matters before the
  // modal is ever opened.
  const [assignLicense, setAssignLicense] = useState(false);
  // Client-side validation warning surfaced on Submit when one or more
  // typed tags fail email-shape validation. Kept separate from the
  // hook's backend `error` so the two never clobber each other.
  const [invalidEmailsWarning, setInvalidEmailsWarning] = useState<string | null>(null);

  // Reset every transient field when the modal is closed so the next
  // open starts blank — without this, leftover typed emails and the
  // "license shortage" / submit error from the previous attempt linger.
  // Keying on `open` covers both Cancel-click and Esc / overlay-click
  // paths (parent flips `open` -> false in all of them). See AHA-42825.
  //
  // On open (AHA-41755): seed the "Assign licences" checkbox based on
  // current seat availability — default-checked when there's at least one
  // seat free, default-unchecked when the team is at quota. Reading
  // availableLicenses inside the open-branch and intentionally keeping it
  // out of the dep list keeps the seed a one-shot per open transition; we
  // don't want a mid-modal change in quota to override the user's manual
  // toggle.
  useEffect(() => {
    if (open) {
      setAssignLicense(hasTeam && isPaidTeamPlan && licenseQuantity - usedLicenses > 0);
      return;
    }
    setEmails([]);
    setRole("editor");
    setTeamName("");
    setAssignLicense(false);
    setInvalidEmailsWarning(null);
    clearError();
    // hasTeam / licenseQuantity / usedLicenses intentionally excluded —
    // the seed should run only on the open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clearError]);

  // Mirrors InviteNewMemberV2.changeInvitedRole has-team branch.
  const handleRoleChange = (value: TeamRole) => {
    setRole(value);
    track("Select role", {
      eventAction: "Team management > Invite member > Select role",
      eventCategory: MY_TEAM,
      eventLabel: value,
    });
  };

  // Mirrors InviteNewMemberV2.hideModal cancel tracking.
  const handleClose = () => {
    track("Team management > Invite member > Cancel invitation", {
      eventCategory: MY_TEAM,
      eventLabel: emails.join(","),
    });
    onClose();
  };

  // Mirrors InviteNewMemberV2.handleUpgradeTeam.
  const handleBuyLicenses = () => {
    track("Team management > Invite member > Buy licenses", {
      eventCategory: MY_TEAM,
      eventAction: "Team management > Invite member > Buy licenses",
    });
    onBuyLicenses();
  };

  const hasTeam = Boolean(account?.id);
  const usedLicenses = Number(account?.numberOfUsedLicenses ?? 0);
  const licenseQuantity = Number(account?.licenseQuantity ?? 0);
  // AHA-43204 (comment 167112): show the licence block for every team that
  // exists, including free teams. On a free team the count surfaces as
  // "0 licences available" with the "Buy more" link acting as a CTA — the
  // earlier "hide entirely" approach lost the upsell affordance. The no-team
  // (create-team) branch still skips it: there's no team to count seats for.
  const showLicenseUsage = hasTeam;
  // AHA-41755: seats currently available for granting at accept time. When
  // this is 0 the "Assign licences" checkbox is disabled and forced
  // unchecked — the team has nothing to grant, so opting-in would be
  // misleading. Clamped at 0 so an over-quota team (used > quota, possible
  // after CS-assisted seat reductions) doesn't surface a negative count.
  //
  // AHA-43204 (comment 167112): force 0 on non-paid teams. Free teams ship
  // with `licenseQuantity = 200` (a default cap, not purchased seats), so
  // the raw subtraction surfaced "200 licences available" — misleading,
  // since there are no purchased licences to grant. The plan flag is the
  // canonical signal for "this team has real seats".
  const availableLicenses = isPaidTeamPlan
    ? Math.max(0, licenseQuantity - usedLicenses)
    : 0;
  // AHA-43204: free teams (isPaidTeamPlan = false) collapse to
  // availableLicenses = 0 above, which is enough on its own to disable the
  // checkbox. Keeping the explicit isPaidTeamPlan check is belt-and-braces
  // for readability — the FE never sends `assignLicense: true` on a free
  // team, so the backend's intent-insert skip stays effective.
  const canAssignLicense = hasTeam && isPaidTeamPlan && availableLicenses > 0;

  // Mirrors `InviteNewMemberV2.normalizeEmailEntries` in the presenter:
  // trim + lowercase + dedupe. We deliberately DO NOT drop invalid entries
  // here — keeping them in the derived list lets us surface a Submit-time
  // warning that names the bad addresses (see AHA-42825 comment, 2026-05-25).
  // Lowercasing is load-bearing — Auth/account lookups downstream are
  // case-sensitive on most rows, so an `Alice@Foo.com` invitation never
  // lines up with the `alice@foo.com` account that registers later. See
  // AHA-42824.
  const normalizedEmails = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of emails) {
      const normalized = raw.trim().toLowerCase();
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      out.push(normalized);
    }
    return out;
  }, [emails]);
  const invalidEmails = useMemo(
    () => normalizedEmails.filter((e) => !EMAIL_RE.test(e)),
    [normalizedEmails],
  );
  // The only hard block on inviting is the team-size ceiling. We deliberately
  // do NOT warn about exceeding the licence quota here — under license
  // management (the regime this embed runs on, AHA-41755) invitations don't
  // consume seats; licences are granted/revoked separately at accept time, so
  // a "you'll exceed your seats" message while the owner is still typing is
  // misleading. The owner can grant seats from the team list afterwards.
  // The page-level LicenseShortageAlert still surfaces the real "over-quota"
  // condition (granted licences > quota) — that's a different signal.
  const overLimit = hasTeam && teamSize + normalizedEmails.length > MAX_TEAM_SIZE;
  // Button stays clickable even with invalid tags — clicking surfaces the
  // warning rather than silently doing nothing (presenter parity).
  const canSubmit = normalizedEmails.length > 0 && !overLimit && (hasTeam || teamName.trim().length > 0);

  // AHA-43588 (revises AHA-43204): keep the role field VISIBLE and
  // consistent for EVERY viewer, instead of hiding it for non-owners.
  // Owners — and the no-team (create-team) branch, where the inviter
  // becomes the new team's owner — can pick "Admin". A non-owner admin on
  // an existing team still SEES the "Admin" option, but it's rendered
  // DISABLED (greyed, non-selectable) with a tooltip explaining why:
  // general-api rejects admin-invites-admin with 400 "Cannot invite another
  // admin", so admins may only invite Members. AHA-43204 hid the whole
  // field for this case, which read as a bug ("role selection field is
  // missing"); showing it disabled keeps the admin's view consistent with
  // the owner's and signals the restriction rather than silently dropping
  // the control.
  //
  // Contract preserved: the "Admin" option is `disabled` for non-owners so
  // it can never be selected via mouse or keyboard, and `role` state
  // initialises to "editor" — so a non-owner's submit payload always sends
  // accountRole "editor", never "admin". The upstream 400 gate stays intact.
  const canInviteAdmin = currentUserIsTeamOwner || !hasTeam;
  const roleOptions = useMemo(
    () => [
      {
        value: "admin" as TeamRole,
        disabled: !canInviteAdmin,
        label: canInviteAdmin ? (
          t("team.role.admin")
        ) : (
          <Tooltip title={t("team.invite.adminOptionDisabledHint")} placement="left">
            {/* AntD applies `pointer-events: none` to a disabled option's row,
                and the property inherits — so without resetting it the child
                span never receives hover and the Tooltip never fires. Setting
                `pointer-events: auto` on the span re-enables hover so the
                tooltip can explain why Admin is disabled. (Gemini review, PR #25) */}
            <span style={{ pointerEvents: "auto" }}>{t("team.role.admin")}</span>
          </Tooltip>
        ),
      },
      { value: "editor" as TeamRole, label: t("team.role.member") },
    ],
    [canInviteAdmin, t],
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Validate on click rather than as-you-type so the user isn't nagged
    // mid-typing. Mirrors `InviteNewMemberV2.validateInviteForm` — bail
    // with an inline warning if any tag fails the email-shape check.
    if (invalidEmails.length > 0) {
      setInvalidEmailsWarning(
        t("team.invite.invalidEmailsWarning", { emails: invalidEmails.join(", ") }),
      );
      return;
    }
    const ok = await invite({
      emails: normalizedEmails,
      accountRole: role,
      teamName: hasTeam ? undefined : teamName.trim(),
      // AHA-41755: only meaningful for has-team invites — the no-team
      // (create-team) branch always grants the owner a licence via a
      // separate path (`grantedSource: "owner"`), so the checkbox doesn't
      // need to fire here. The backend treats `false` as default-deny.
      // Gating on `canAssignLicense` (not just `hasTeam`) means a quota
      // exhausted between modal-open and submit doesn't send a doomed
      // opt-in upstream.
      assignLicense: canAssignLicense && assignLicense,
    });
    if (ok) {
      setEmails([]);
      setTeamName("");
      setAssignLicense(false);
      setInvalidEmailsWarning(null);
      onInvited();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={t(hasTeam ? "team.invite.title" : "team.invite.addTeamMembersTitle")}
      width={504}
      // Don't `centered`: this embed is loaded in a zoid iframe whose height
      // is resized to match its content, so AntD's "viewport" inside the
      // iframe is the FULL document, not the visible region. With `centered`
      // + `mask={false}`, the dialog lands ~halfway down the iframe (often
      // 1000–2000px below the toolbar where Invite was clicked), and there's
      // no backdrop dimming to hint that it scrolled off-screen. AntD's
      // default `top: 100` sits the modal near the top of the iframe doc,
      // which is where the toolbar (and the user's eye) already is.
      // Body cap is in fixed px, NOT `vh` — the host resizes the iframe to
      // fit our content (zoid `onHeightChange` tracks `document.body.scrollHeight`),
      // so `vh` follows that same number and the cap effectively disappears as
      // the page grows. 480px fits the form comfortably given the email tags
      // already cap at 132px (see InviteMemberModal.module.css). See AHA-43040.
      styles={{ body: { maxHeight: 480, overflowY: "auto" } }}
      // Unmount the form subtree on close so AntD's own internal state resets
      // too — most importantly the tags Select's typed-but-not-committed search
      // text, which the controlled-state reset effect below can't reach (it
      // only clears `emails`/`role`/`teamName`). AntD 6 renamed this from
      // destroyOnClose. See AHA-41755.
      destroyOnHidden
      // `data-testid` is added via wrapClassName-less `modalRender` so
      // the testid lands on the modal's content node (not the wrapper).
      // AntD forwards arbitrary attrs through `rootClassName` only on
      // the wrapper, so we attach it via wrapProps.
      wrapProps={{ "data-testid": "modal-invite-new-member-modal" }}
      // AHA2-21: remove the backdrop overlay again. AHA2-7 had RE-ENABLED the
      // mask, but this modal lives inside the presenter's zoid iframe: AntD's
      // mask only dims the iframe region while the surrounding presenter chrome
      // stays bright, so the dark backdrop reads as wrong/"dị" inside the embed.
      // In the Slack thread (ts 1783328346 / 1783328679) the team lead
      // (U06NVFEPXM0) agreed the fix is to drop the backdrop ("bỏ cái backdrop
      // tạm đi"), and the reporter reconfirmed here ("Xoá cái overlay nền đen đi
      // giùm"). `mask={false}` renders no dark overlay. There is no mask left to
      // click, so close is via Cancel / the × / Esc — acceptable per the thread.
      mask={false}
      footer={
        <Space>
          <Button
            onClick={handleClose}
            data-testid="modal-invite-new-member-cancel-button"
          >
            {t("team.action.cancel")}
          </Button>
          {/* Mirrors InviteNewMemberV2.vue `v-aha-emit-action name="invite-new-member"` */}
          <TrackOnClick name="invite-new-member">
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={pending}
              disabled={!canSubmit}
              data-testid="modal-invite-new-member-invite-button"
            >
              {t("team.action.invite")}
            </Button>
          </TrackOnClick>
        </Space>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {!hasTeam && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontWeight: 600 }} htmlFor="invite-team-name">
              {t("team.invite.teamNameLabel")}
            </label>
            <Input
              id="invite-team-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder={t("team.invite.teamNamePlaceholder")}
              // AHA2-21: clone the reference text input height (40px) + padding
              // + rounded-lg corners (InviteNewMemberV2.vue text-input).
              className={styles.textInput}
              data-testid="modal-invite-new-member-team-name-input"
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Original renders this label only in the no-team branch —
              with-team users already have "Invite members" as the
              modal title, so the label would duplicate it. */}
          {!hasTeam && (
            <label style={{ fontWeight: 600 }}>{t("team.invite.emailsLabel")}</label>
          )}
          <Space.Compact style={{ width: "100%" }}>
            {/*
              AHA-43040: cap the tags area at ~4 rows so pasting a long
              email list doesn't make the modal explode in height.
              The cap is set INLINE on the Select because AntD v5/v6
              ships its own CSS-in-JS rules for `.ant-select-selector`
              that out-specificity our CSS module — even with
              `!important`, the runtime-injected rules can override on
              re-render. Inline `style` sits on the rendered `.ant-select`
              wrapper itself (specificity 1,0,0,0), which wins, and
              `overflowY: auto` makes the inner tags scroll. 128px ≈ four
              rows of tags at AntD's default tag size.
            */}
            <Select
              mode="tags"
              value={emails}
              onChange={(v) => {
                clearError();
                setInvalidEmailsWarning(null);
                setEmails(v as string[]);
              }}
              placeholder={t("team.invite.emailsPlaceholder")}
              tokenSeparators={[",", ";", " "]}
              style={{ flex: 1, maxHeight: 128, overflowY: "auto" }}
              open={false}
              suffixIcon={null}
              className={styles.emailSelect}
              data-testid="modal-invite-new-member-emails-dropdown"
            />
            {/* AHA-43588: always render the role Select. `roleOptions` now
                always carries both "Admin" and "Member" (Admin `disabled`
                for non-owners), so the field is present and consistent for
                owners and admins alike — no more "missing role field" for
                admins. A non-owner can only pick "Member"; the disabled
                "Admin" option is non-selectable, so submit stays `editor`. */}
            <Select<TeamRole>
              value={role}
              onChange={handleRoleChange}
              options={roleOptions}
              // AHA2-21: match the reference role select width (104px) —
              // InviteNewMemberV2.vue `.invite-new-member-modal__role-select`
              // is `flex: 0 0 104px; width: 104px`. Round-4 had it at 130px.
              style={{ width: 104 }}
              className={styles.roleSelect}
              data-testid="modal-invite-new-member-role-dropdown"
              // AHA-43040: leave `getPopupContainer` at AntD's default
              // (`document.body` portal). The earlier override
              // (`trigger.parentElement`) pinned the popup inside the
              // tiny Space.Compact wrapper — combined with the wrapper's
              // overflow rules, two scrollbars appeared and the two
              // options rendered in the wrong positions. The body portal
              // works inside our iframe context, and AntD positions the
              // popup directly under the trigger by default.
              placement="bottomLeft"
            />
          </Space.Compact>
        </div>

        {showLicenseUsage && (
          <div
            // AHA2-7: use the same numeric 8px gap as the other field groups
            // in this modal instead of the one-off "1rem" (16px), so the
            // vertical rhythm is consistent across the form (reporter:
            // "Spacing có vấn đề").
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
            data-testid="modal-invite-new-member-license-block"
          >
            {/* AHA-41755: owner ticks this to auto-grant a V2 licence on
                accept. Backend persists an intent row; the grant fires only
                when the invitee actually accepts (and only as long as there
                are seats free at that moment — first-come-first-served).
                The tooltip explains the seat semantics so owners know the
                checkbox doesn't reserve a seat at invite time. */}
            {/* AHA-41755: disabled + force-unchecked when there are no seats
                left — opt-in would silently fail at accept time, so we don't
                let the owner check the box in the first place. The open-effect
                already seeds assignLicense=false in that state, so the `false`
                fallback on `checked` is belt-and-braces against a mid-modal
                quota change (e.g. someone else granted the last seat). */}
            <Checkbox
              checked={canAssignLicense && assignLicense}
              disabled={!canAssignLicense}
              onChange={(e) => setAssignLicense(e.target.checked)}
              data-testid="modal-invite-new-member-assign-license-checkbox"
            >
              <Space size={4}>
                {t("team.invite.assignLicensesLabel")}
                <Tooltip title={t("team.invite.assignLicensesHint")}>
                  <QuestionCircleOutlined
                    style={{ color: "var(--ant-color-text-tertiary)" }}
                  />
                </Tooltip>
              </Space>
            </Checkbox>
            {/* AHA-43204: only owners get the "Buy more" CTA — admins
                aren't authorised to purchase, so the link would lead to a
                dead-end. The available-count text stays for both, since
                the seat status is still useful context. */}
            <div style={{ fontSize: 14 }}>
              <span>
                {t("team.license.availableCount", { count: availableLicenses })}.
                {currentUserIsTeamOwner && " "}
              </span>
              {currentUserIsTeamOwner && (
                <Typography.Link
                  onClick={handleBuyLicenses}
                  data-testid="modal-invite-new-member-buy-more-licenses-button"
                >
                  {t("team.action.buyMore")}
                </Typography.Link>
              )}
            </div>
          </div>
        )}

        {invalidEmailsWarning && (
          <p style={{ color: "#d4380d", fontSize: 12, margin: 0 }}>{invalidEmailsWarning}</p>
        )}

        {error && (
          <p style={{ color: "#d4380d", fontSize: 12, margin: 0 }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}
